import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Text } from './Texte';
import { colors, formes, shadow, spacing, typography } from '../theme/tokens';

export interface SegmentedOption {
  key: string;
  label: string;
  // Permet de garder l'identifiant d'un test existant quand on remplace un segment fait à la main.
  testID?: string;
}

export interface SegmentedProps {
  options: SegmentedOption[];
  value: string;
  onChange: (key: string) => void;
  accessibilityLabel?: string;
  // 'sombre' : piste voilée de blanc pour une carte bleu nuit (interlocuteur de l'entretien).
  surFond?: 'clair' | 'sombre';
  testID?: string;
}

/**
 * Onglets en pilule : deux à trois choix, un seul actif. Remplace les segments refaits à la main
 * dans les offres, la relecture de dossier et les choix de lettre.
 *
 * Les segments se partagent la largeur à parts égales (`flex: 1`) et leur libellé passe à la ligne
 * plutôt que de déborder : « Guardadas para mais tarde » ne tient pas sur une ligne en portugais.
 * La rangée suit le sens de l'interface, donc l'ordre se miroite en arabe sans code particulier.
 */
export function Segmented({ options, value, onChange, accessibilityLabel, surFond = 'clair', testID }: SegmentedProps) {
  const sombre = surFond === 'sombre';
  return (
    <View
      testID={testID}
      accessibilityRole="tablist"
      accessibilityLabel={accessibilityLabel}
      style={[styles.piste, sombre && styles.pisteSombre]}
    >
      {options.map((option) => {
        const actif = option.key === value;
        return (
          <Pressable
            key={option.key}
            testID={option.testID ?? (testID ? `${testID}-${option.key}` : undefined)}
            accessibilityRole="tab"
            accessibilityState={{ selected: actif }}
            onPress={() => {
              if (!actif) onChange(option.key);
            }}
            style={[styles.segment, actif && styles.segmentActif]}
          >
            <Text style={[styles.label, sombre && !actif && styles.labelSombre, actif && styles.labelActif]}>{option.label}</Text>
          </Pressable>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  piste: {
    flexDirection: 'row',
    gap: spacing.xs,
    padding: spacing.xs,
    borderRadius: formes.rayon.md,
    backgroundColor: colors.grey200,
  },
  pisteSombre: { backgroundColor: colors.surfaceNuitForte, borderRadius: formes.rayon.md },
  segment: {
    flex: 1,
    // 44 pt : la cible tactile minimale, même si la maquette dessine des segments plus bas.
    minHeight: formes.cible,
    paddingHorizontal: spacing.sm,
    borderRadius: formes.rayon.sm,
    alignItems: 'center',
    justifyContent: 'center',
  },
  segmentActif: {
    backgroundColor: colors.white,
    ...shadow.card,
  },
  label: {
    fontSize: typography.small.size,
    lineHeight: typography.small.lineHeight,
    fontWeight: '600',
    color: colors.grey500,
    textAlign: 'center',
  },
  labelSombre: { color: colors.blancAtenue },
  labelActif: { fontWeight: '700', color: colors.night },
});

